import Cookies from "js-cookie";
import { getCookieDomain } from "../../../utils/getCookieDomain";
import { notRequireAuthPaths } from "@features/notRequiredAuthPaths";

let lessonsRemapHeader: string = "[]";
let coursesRemapHeader: string = "[]";

const TOKEN_EXPIRY_DAYS = 30;
const ONE_DAY_IN_MS = 24 * 60 * 60 * 1000;

export function setLessonsRemap(map: unknown) {
    try {
        lessonsRemapHeader = JSON.stringify(map ?? []);
    } catch {
        lessonsRemapHeader = "[]";
    }
}

export function setCoursesRemap(map: unknown) {
    try {
        coursesRemapHeader = JSON.stringify(map ?? []);
    } catch {
        coursesRemapHeader = "[]";
    }
}

interface ITokenStorage {
    getToken(): Promise<string>;
    setToken(value: string): Promise<void>;
    removeToken(): Promise<void>;
}

export class TokenStorage implements ITokenStorage {
    async getToken(): Promise<string> {
        return Cookies.get("token") || "";
    }

    async removeToken(): Promise<void> {
        const domain = getCookieDomain();

        Cookies.remove("token", { path: "/" });
        if (domain) {
            Cookies.remove("token", { path: "/", domain });
        }
    }

    async setToken(value: string): Promise<void> {
        const domain = getCookieDomain();

        Cookies.set("token", value, {
            path: "/",
            expires: new Date(Date.now() + TOKEN_EXPIRY_DAYS * ONE_DAY_IN_MS),
            ...(domain ? { domain } : {}),
            sameSite: "strict",
            secure: window.location.protocol === "https:"
        });
    }
}

type HttpMethod = "GET" | "POST" | "HEAD" | "DELETE" | "PUT" | "PATCH";

export interface IHttpClient {
    do<T>(url: string, method?: HttpMethod, body?: Record<any, any> | null): Promise<T>;
    doBlob(url: string, method?: HttpMethod): Promise<Blob>;
    tokenStorage: ITokenStorage;
}

export function parseNetworkError(errorMessage: string) {
    let errObj;
    try {
        errObj = JSON.parse(errorMessage);
    } catch (e) {
        errObj = new Error(errorMessage);
    }

    return errObj;
}

function handleUnauthorized(): void {
    if (typeof window === "undefined") return;

    if (new URLSearchParams(window.location.search).has("token")) return;

    const currentPath = window.location.pathname;
    if (notRequireAuthPaths.some((path) => currentPath.includes(path))) return;

    window.location.href = "/auth/signin";
}

// The backend answers a white-label partner's user at any entry point of this
// app with code "other_project" and the landing of their project: they belong
// there, not here.
function redirectOtherProjectUser(error: unknown): void {
    if (typeof window === "undefined") return;
    const { code, redirectUrl } = (error ?? {}) as { code?: unknown; redirectUrl?: unknown };
    if (code === "other_project" && typeof redirectUrl === "string" && redirectUrl.startsWith("https://")) {
        window.location.assign(redirectUrl);
    }
}

function checkFetchStatus(res: Response): Promise<Response> {
    return new Promise((resolve, reject) => {
        if (res.status >= 200 && res.status < 300) {
            return resolve(res);
        } else {
            if (res.status === 401) {
                handleUnauthorized();
            }

            if (res.text) {
                return res
                    .text()
                    .then((errorMessage) => {
                        const error = parseNetworkError(errorMessage);
                        redirectOtherProjectUser(error);
                        reject(error);
                    })
                    .catch((e) => reject(e));
            } else {
                reject(new Error(`Request failed: ${res.status} - ${res.statusText}`));
            }
        }
    });
}

export class request implements IHttpClient {
    constructor(
        public tokenStorage: ITokenStorage,
        private baseUrl: string = ""
    ) { }

    async do<T>(url: string, method: HttpMethod = "GET", body: Record<any, any> | null = null): Promise<T> {
        const token = await this.tokenStorage.getToken();
        const fullUrl = url.startsWith("http") ? url : this.baseUrl + url;

        return await fetch(fullUrl, {
            method,
            ...(body !== null && method !== "GET" && method !== "HEAD" ? { body: JSON.stringify(body) } : {}),
            headers: {
                "Content-Type": "application/json",
                "X-Lesson-Remap": lessonsRemapHeader,
                "X-Course-Remap": coursesRemapHeader,
                ...(!!token ? { Authorization: `Bearer ${token}` } : {})
            }
        })
            .then((res) => checkFetchStatus(res))
            .then(async (response) => {
                const text = await response.text();
                if (!text) return {} as T;
                return JSON.parse(text) as T;
            });
    }

    async doBlob(url: string, method: HttpMethod = "GET"): Promise<Blob> {
        const token = await this.tokenStorage.getToken();
        const fullUrl = url.startsWith("http") ? url : this.baseUrl + url;

        return await fetch(fullUrl, {
            method,
            headers: {
                ...(!!token ? { Authorization: `Bearer ${token}` } : {})
            }
        })
            .then((res) => checkFetchStatus(res))
            .then((response) => response.blob());
    }
}

export const HttpRequest = new request(new TokenStorage());
